import type { Mesh } from "./mesh3d";

export interface FillResult {
  mesh: Mesh;
  filled: number; // holes closed
  skipped: number; // open loops left alone (too big, or touching another hole or a non-manifold edge)
  addedTriangles: number;
}

/**
 * Closes small open loops in a WELDED mesh (call weldVertices first so shared edges are shared indices).
 * Each loop of at most `maxLoopEdges` edges gets a fan of triangles around its centre point, wound opposite to
 * the neighbouring faces so the surface stays consistently oriented. Loops that share a corner with another
 * open loop, or sit on an edge used by three or more faces, are left alone and counted in `skipped`.
 * Never touches a mesh that is already closed: the input is returned unchanged.
 */
export function fillHoles(mesh: Mesh, maxLoopEdges = 100): FillResult {
  const count = new Map<string, number>();
  const key = (a: number, b: number) => (a < b ? `${a}_${b}` : `${b}_${a}`);
  for (const [a, b, c] of mesh.triangles) for (const [p, q] of [[a, b], [b, c], [c, a]]) count.set(key(p, q), (count.get(key(p, q)) ?? 0) + 1);
  const next = new Map<number, number[]>(); // directed boundary edges u -> v, as the existing triangles run them
  let boundary = 0;
  for (const [a, b, c] of mesh.triangles) for (const [p, q] of [[a, b], [b, c], [c, a]]) {
    if (count.get(key(p, q)) === 1) { boundary++; next.set(p, [...(next.get(p) ?? []), q]); }
  }
  if (boundary === 0) return { mesh, filled: 0, skipped: 0, addedTriangles: 0 };

  const badEdge = (p: number, q: number) => (count.get(key(p, q)) ?? 0) > 2;
  const vertices = mesh.vertices.slice();
  const triangles = mesh.triangles.map((t) => t.slice());
  const used = new Set<string>();
  let filled = 0, skipped = 0, added = 0;
  for (const [start, outs] of Array.from(next.entries())) {
    for (const first of outs) {
      if (used.has(`${start}>${first}`)) continue;
      // Walk the boundary until it closes or runs out; every walked edge is marked so a loop is judged once.
      const loop = [start];
      let cur = start, nxt: number | undefined = first, closed = false, clean = outs.length === 1;
      while (nxt !== undefined) {
        used.add(`${cur}>${nxt}`);
        if (badEdge(cur, nxt)) clean = false;
        cur = nxt;
        if (cur === start) { closed = true; break; }
        loop.push(cur);
        const o = next.get(cur) ?? [];
        if (o.length !== 1) clean = false;
        nxt = o.find((w) => !used.has(`${cur}>${w}`));
      }
      if (!closed || !clean || loop.length < 3 || loop.length > maxLoopEdges) { skipped++; continue; }
      if (loop.length === 3) { triangles.push([loop[0], loop[2], loop[1]]); added++; filled++; continue; }
      let cx = 0, cy = 0, cz = 0;
      for (const i of loop) { cx += vertices[i * 3]; cy += vertices[i * 3 + 1]; cz += vertices[i * 3 + 2]; }
      const c = vertices.length / 3;
      vertices.push(cx / loop.length, cy / loop.length, cz / loop.length);
      for (let i = 0; i < loop.length; i++) { triangles.push([loop[(i + 1) % loop.length], loop[i], c]); added++; }
      filled++;
    }
  }
  return { mesh: { vertices, triangles }, filled, skipped, addedTriangles: added };
}

/** Largest bounding-box side of a mesh in its own units; 0 when empty. */
export function largestSide(mesh: Mesh): number {
  const v = mesh.vertices;
  if (v.length < 3) return 0;
  const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i + 2 < v.length; i += 3) for (let k = 0; k < 3; k++) { const c = v[i + k]; if (c < lo[k]) lo[k] = c; if (c > hi[k]) hi[k] = c; }
  return Math.max(hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]);
}

export type ScaleMode = "none" | "factor" | "largest";

/** The multiplier for a scale choice: a plain factor, or whatever makes the largest side equal `value`. null when the input can't be used. */
export function scaleFactorFor(mode: ScaleMode, value: number | null, mesh: Mesh): number | null {
  if (mode === "none") return 1;
  if (value === null || !Number.isFinite(value) || value <= 0) return null;
  if (mode === "factor") return value <= 1e6 ? value : null;
  const ext = largestSide(mesh);
  if (!(ext > 0)) return null;
  const f = value / ext;
  return f <= 1e6 ? f : null;
}
